import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../services/api';

// Currency formatting helpers
const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatCurrencyDetailed = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatCompact = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (value >= 1000000) {
    return '$' + (value / 1000000).toFixed(1) + 'M';
  }
  if (value >= 1000) {
    return '$' + (value / 1000).toFixed(0) + 'K';
  }
  return formatCurrency(value);
};

// Scenario display configurations - Updated with Hubdeck colors
const SCENARIO_CONFIG = {
  // Actual provider scenarios from cost_structures table
  'PROVIDER_FLAT_FEE_PRTCP_ONLY': {
    name: 'Flat Fee Per Participant',
    description: 'Flat fee per participant only',
    color: '#10b981',  // Green for best option
    priority: 1
  },
  'PROVIDER_FLAT_FEE_P+AUA': {
    name: 'Flat Fee + AUA',
    description: 'Flat fee per participant + asset-based',
    color: '#0E8376',  // Darker teal
    priority: 2
  },
  'PROVIDER_FLAT_FEE_ADMIN+P': {
    name: 'Flat Fee Admin + Participant',
    description: 'Base admin fee + per participant',
    color: '#33BFAF',  // Light teal
    priority: 3
  },
  'PROVIDER_BENCHMARK': {
    name: 'Benchmark Provider',
    description: 'Generic low-cost option',
    color: '#12A493',  // Hubdeck teal
    priority: 4
  },
  'PROVIDER_MID_STD': {
    name: 'Industry Standard',
    description: 'Mid-market pricing',
    color: '#7C5CDB',  // Hubdeck purple
    priority: 5
  },
  'PROVIDER_HIGH_TOUCH': {
    name: 'High Touch Service',
    description: 'Premium service model',
    color: '#f59e0b',  // Amber for premium
    priority: 6
  },
  // Legacy scenario types (may not exist in current database)
  'PROVIDER_ULTRA_LOW': {
    name: 'Ultra Low Cost',
    description: '$20/participant flat fee',
    color: '#10b981',
    priority: 7
  },
  'PROVIDER_FLAT_FEE': {
    name: 'Standard Flat Fee',
    description: '$100/participant',
    color: '#0E8376',
    priority: 8
  },
  'PROVIDER_LOW_BLEND': {
    name: 'Low Cost Blended',
    description: '$50/participant + 5 BPS',
    color: '#33BFAF',
    priority: 9
  },
  // Peer scenarios (filtered out but kept in config for reference)
  'PEER_P25': {
    name: 'Peer 25th Percentile',
    description: 'Better than 75% of peers',
    color: '#84cc16',
    priority: 97
  },
  'PEER_P50': {
    name: 'Peer Median',
    description: 'Middle of peer group',
    color: '#eab308',
    priority: 98
  },
  'PEER_P75': {
    name: 'Peer 75th Percentile',
    description: 'Better than 25% of peers',
    color: '#f97316',
    priority: 99
  }
};

// Helper function to get scenario config with fallback for unknown types
const getScenarioConfig = (scenarioType) => {
  // If config exists, return it
  if (SCENARIO_CONFIG[scenarioType]) {
    return SCENARIO_CONFIG[scenarioType];
  }
  
  // Generate fallback config for unknown types
  // Convert PROVIDER_FLAT_FEE_PRTCP_ONLY → "Flat Fee Prtcp Only"
  const cleanName = scenarioType
    ?.replace('PROVIDER_', '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  
  // Generate a consistent color based on the scenario type string
  const hash = scenarioType?.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0) || 0;
  
  const hue = Math.abs(hash) % 360;
  const color = `hsl(${hue}, 60%, 50%)`;
  
  return {
    name: cleanName,
    description: scenarioType,
    color: color,
    priority: 50
  };
};

const ScenariosTab = ({ ein, planNumber, planStatus }) => {
  const [scenarioData, setScenarioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedScenarios, setSelectedScenarios] = useState(['PROVIDER_FLAT_FEE_PRTCP_ONLY', 'PROVIDER_BENCHMARK', 'PROVIDER_MID_STD']);
  const [showAllScenarios, setShowAllScenarios] = useState(false);

  // Check if plan is terminated
  const isTerminated = planStatus?.plan_status === 'TERMINATED';

  useEffect(() => {
    const fetchScenarioData = async () => {
      try {
        setLoading(true);
        const data = await api.getScenarios(ein, planNumber);
        setScenarioData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (ein && planNumber) {
      fetchScenarioData();
    }
  }, [ein, planNumber]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading scenario analysis...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  if (!scenarioData || !scenarioData.scenarios || scenarioData.scenarios.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No scenario data available for this plan</div>
      </div>
    );
  }

  // Organize scenarios by type - FILTER OUT PEER SCENARIOS
  // Only show real provider alternatives that users can switch to
  const scenariosByType = {};
  scenarioData.scenarios.forEach(scenario => {
    // Only include PROVIDER_* scenarios, exclude PEER_* benchmarks
    if (scenario.scenario_type?.startsWith('PROVIDER_')) {
      if (!scenariosByType[scenario.scenario_type]) {
        scenariosByType[scenario.scenario_type] = [];
      }
      scenariosByType[scenario.scenario_type].push(scenario);
    }
  });

  // Sort scenarios by priority
  const sortedScenarioTypes = Object.keys(scenariosByType).sort((a, b) => {
    return (getScenarioConfig(a)?.priority || 99) - (getScenarioConfig(b)?.priority || 99);
  });

  // Prepare chart data
  const prepareChartData = () => {
    const timeframes = [5, 10, 15, 20, 25];
    return timeframes.map(years => {
      const dataPoint = { years };
      
      // Add current plan
      const currentScenario = scenarioData.scenarios.find(
        s => s.projection_years === years && s.scenario_type === sortedScenarioTypes[0]
      );
      if (currentScenario) {
        dataPoint['Current Plan'] = currentScenario.projected_balance_current_costs;
      }

      // Add selected scenarios
      selectedScenarios.forEach(scenarioType => {
        const scenario = scenarioData.scenarios.find(
          s => s.projection_years === years && s.scenario_type === scenarioType
        );
        if (scenario) {
          dataPoint[getScenarioConfig(scenarioType)?.name || scenarioType] = 
            scenario.projected_balance_alt_costs;
        }
      });

      return dataPoint;
    });
  };

  const chartData = prepareChartData();

  // Calculate summary stats - FIXED: Find ACTUAL lowest cost PROVIDER scenario
  // Only consider PROVIDER_* scenarios (from cost_structures), NOT peer benchmarks
  const providerScenarios = scenarioData.scenarios.filter(s => 
    s.projection_years === 25 && 
    s.scenario_type?.startsWith('PROVIDER_')
  );
  
  const best25YearData = providerScenarios.reduce((best, current) => {
    const bestCost = best?.avg_alt_annual_fee || Infinity;
    const currentCost = current?.avg_alt_annual_fee || Infinity;
    return currentCost < bestCost ? current : best;
  }, null);
  
  const bestScenario = best25YearData?.scenario_type;
  
  // Check if current plan is better than all alternatives
  const currentAnnualCost = scenarioData?.current_costs?.current_annual_admin_cost;
  const bestAlternativeCost = best25YearData?.avg_alt_annual_fee;
  const currentPlanIsBest = currentAnnualCost && bestAlternativeCost && currentAnnualCost < bestAlternativeCost;

  const toggleScenario = (scenarioType) => {
    if (selectedScenarios.includes(scenarioType)) {
      setSelectedScenarios(selectedScenarios.filter(s => s !== scenarioType));
    } else {
      setSelectedScenarios([...selectedScenarios, scenarioType]);
    }
  };

  const displayedScenarios = showAllScenarios ? sortedScenarioTypes : sortedScenarioTypes.slice(0, 4);

  return (
    <div className="space-y-6 p-6">
      {/* Terminated Plan Notice */}
      {isTerminated && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8 border-l-4 border-amber-500 shadow-soft">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg 
                className="h-7 w-7 text-amber-600" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-bold text-amber-900 mb-2">
                Historical Projections Only
              </h3>
              <div className="mt-2 text-sm text-amber-800 leading-relaxed">
                <p>
                  Since this plan was terminated in {planStatus?.termination_year}, these projections 
                  show what <strong>could have been saved</strong> if the plan were still active. 
                  They cannot be realized as the plan no longer exists.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="bg-gradient-to-r from-teal/10 to-teal/20 rounded-2xl p-8 border-2 border-teal/30 shadow-soft">
        <h2 className="text-2xl font-bold text-teal-dark mb-6">
          Cost Impact Analysis & Future Projections
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-soft border-2 border-neutral-200">
            <div className="text-sm text-gray-600 mb-2 font-medium uppercase tracking-wide">Current Annual Cost</div>
            <div className="text-3xl font-bold text-coral">
              {scenarioData?.current_costs?.current_annual_admin_cost !== undefined 
                ? formatCurrency(scenarioData.current_costs.current_annual_admin_cost)
                : 'N/A'}
            </div>
            <div className="text-xs text-gray-500 mt-2">from Form 5500 filings</div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-soft border-2 border-neutral-200">
            <div className="text-sm text-gray-600 mb-2 font-medium uppercase tracking-wide">Best Alternative Cost</div>
            <div className="text-3xl font-bold text-green-600">
              {currentPlanIsBest ? (
                <span className="text-teal-dark">Current Plan</span>
              ) : (
                best25YearData && formatCurrencyDetailed(best25YearData.avg_alt_annual_fee)
              )}
            </div>
            <div className="text-xs text-gray-500 mt-2">
              {currentPlanIsBest ? (
                "Already optimized!"
              ) : (
                getScenarioConfig(bestScenario)?.description || bestScenario?.replace(/_/g, ' ') || 'Generic low-cost option'
              )}
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-soft border-2 border-neutral-200">
            <div className="text-sm text-gray-600 mb-2 font-medium uppercase tracking-wide">25-Year Potential Savings</div>
            <div className="text-3xl font-bold text-teal-dark">
              {currentPlanIsBest ? (
                <span className="text-gray-500">$0</span>
              ) : (
                best25YearData && formatCompact(best25YearData.projected_total_savings)
              )}
            </div>
            <div className="text-xs text-gray-500 mt-2">
              {currentPlanIsBest ? (
                "Plan already optimized"
              ) : (
                best25YearData && formatCurrency(best25YearData.projected_savings_per_participant) + ' per participant'
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Projection Chart */}
      <div className="bg-white rounded-2xl shadow-soft p-8 border-2 border-neutral-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            Projected Balance Growth Comparison
          </h3>
          <div className="text-sm text-gray-600 bg-neutral-100 px-4 py-2 rounded-xl">
            Assumed return rate: {best25YearData && (best25YearData.assumed_return_rate * 100).toFixed(1)}%
          </div>
        </div>
        
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
            <XAxis 
              dataKey="years" 
              label={{ value: 'Years from Now', position: 'insideBottom', offset: -5, fill: '#737373' }}
              tick={{ fill: '#737373' }}
            />
            <YAxis 
              label={{ value: 'Projected Balance ($)', angle: -90, position: 'insideLeft', fill: '#737373' }}
              tickFormatter={(value) => formatCompact(value)}
              tick={{ fill: '#737373' }}
            />
            <Tooltip 
              formatter={(value) => formatCurrency(value)}
              labelFormatter={(label) => `${label} Years`}
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '2px solid #E5E5E5',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Legend />
            
            {/* Current plan line */}
            <Line 
              type="monotone" 
              dataKey="Current Plan" 
              stroke="#FF7A5C" 
              strokeWidth={4}
              strokeDasharray="5 5"
              dot={{ r: 5, fill: '#FF7A5C' }}
            />
            
            {/* Selected scenario lines */}
            {selectedScenarios.map((scenarioType, index) => {
              const config = getScenarioConfig(scenarioType);
              return (
                <Line 
                  key={scenarioType}
                  type="monotone" 
                  dataKey={config?.name || scenarioType}
                  stroke={config?.color || '#12A493'}
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>

        {/* Scenario selector */}
        <div className="mt-6 p-6 bg-neutral-50 rounded-xl border-2 border-neutral-200">
          <div className="text-sm font-semibold text-gray-700 mb-3">Select scenarios to display:</div>
          <div className="flex flex-wrap gap-2">
            {sortedScenarioTypes.map(scenarioType => {
              const config = getScenarioConfig(scenarioType);
              const isSelected = selectedScenarios.includes(scenarioType);
              return (
                <button
                  key={scenarioType}
                  onClick={() => toggleScenario(scenarioType)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all shadow-soft ${
                    isSelected 
                      ? 'text-white' 
                      : 'bg-neutral-200 text-gray-700 hover:bg-neutral-300'
                  }`}
                  style={isSelected ? { backgroundColor: config?.color } : {}}
                >
                  {config?.name || scenarioType}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scenario Comparison Table */}
      <div className="bg-white rounded-2xl shadow-soft p-8 border-2 border-neutral-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            Detailed Scenario Comparison
          </h3>
          <button
            onClick={() => setShowAllScenarios(!showAllScenarios)}
            className="text-sm text-teal hover:text-teal-dark font-medium px-4 py-2 bg-teal/10 rounded-xl hover:bg-teal/20 transition-colors"
          >
            {showAllScenarios ? 'Show Top 4' : 'Show All Scenarios'}
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Scenario</th>
                <th className="px-4 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Annual Cost</th>
                <th className="px-4 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">5 Years</th>
                <th className="px-4 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">10 Years</th>
                <th className="px-4 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">15 Years</th>
                <th className="px-4 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">20 Years</th>
                <th className="px-4 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">25 Years</th>
                <th className="px-4 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Total Savings</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Current Plan Row */}
              <tr className="bg-red-50">
                <td className="px-4 py-4">
                  <div className="font-semibold text-gray-900">Current Plan</div>
                  <div className="text-xs text-gray-500">Your existing costs</div>
                </td>
                <td className="px-4 py-4 text-right font-semibold text-coral">
                  {scenarioData?.current_costs?.current_annual_admin_cost !== undefined 
                    ? formatCurrency(scenarioData.current_costs.current_annual_admin_cost)
                    : 'N/A'}
                </td>
                {[5, 10, 15, 20, 25].map(years => {
                  const data = scenariosByType[bestScenario]?.find(s => s.projection_years === years);
                  return (
                    <td key={years} className="px-4 py-4 text-right text-gray-600 font-medium">
                      {data && formatCompact(data.projected_balance_current_costs)}
                    </td>
                  );
                })}
                <td className="px-4 py-4 text-right text-gray-400">—</td>
              </tr>

              {/* Alternative Scenarios */}
              {displayedScenarios.map(scenarioType => {
                const config = getScenarioConfig(scenarioType);
                const scenarios = scenariosByType[scenarioType] || [];
                const data25yr = scenarios.find(s => s.projection_years === 25);
                
                return (
                  <tr key={scenarioType} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-4 py-4">
                      <div 
                        className="font-semibold" 
                        style={{ color: config?.color || '#000' }}
                      >
                        {config?.name || scenarioType}
                      </div>
                      <div className="text-xs text-gray-500">{config?.description}</div>
                    </td>
                    <td className="px-4 py-4 text-right font-semibold text-gray-900">
                      {data25yr && formatCurrency(data25yr.avg_alt_annual_fee)}
                    </td>
                    {[5, 10, 15, 20, 25].map(years => {
                      const data = scenarios.find(s => s.projection_years === years);
                      return (
                        <td key={years} className="px-4 py-4 text-right text-gray-700 font-medium">
                          {data && formatCompact(data.projected_balance_alt_costs)}
                        </td>
                      );
                    })}
                    <td className="px-4 py-4 text-right">
                      {data25yr && (
                        <div>
                          <div className="font-bold text-green-600">
                            {formatCompact(data25yr.projected_total_savings)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatCurrency(data25yr.projected_savings_per_participant)}/person
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cost of Waiting Section */}
      <div className="bg-white rounded-2xl shadow-soft p-8 border-2 border-neutral-100">
        <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <span className="text-2xl mr-2">💰</span>
          The Cost of Waiting
        </h3>
        
        <p className="text-sm text-gray-600 mb-6 leading-relaxed">
          Delaying a switch to a lower-cost provider has a real impact on retirement savings due to lost compound growth.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 5].map(waitYears => {
            const adjustedYears = 25 - waitYears;
            const data = scenariosByType[bestScenario]?.find(s => s.projection_years === adjustedYears);
            const opportunityCost = best25YearData && data 
              ? best25YearData.projected_balance_alt_costs - data.projected_balance_alt_costs
              : 0;

            return (
              <div 
                key={waitYears} 
                className={`p-6 rounded-2xl border-2 shadow-soft ${
                  waitYears === 0 
                    ? 'border-green-300 bg-green-50' 
                    : 'border-orange-300 bg-orange-50'
                }`}
              >
                <div className="text-sm font-semibold text-gray-700 mb-3">
                  {waitYears === 0 ? '✅ Switch Today' : `⏰ Wait ${waitYears} Year${waitYears > 1 ? 's' : ''}`}
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-2">
                  {data && formatCompact(data.projected_balance_alt_costs)}
                </div>
                {waitYears > 0 && (
                  <div className="text-sm font-semibold text-coral">
                    -{formatCompact(opportunityCost)}
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-2">
                  at {adjustedYears} years
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 p-6 bg-yellow-50 rounded-2xl border-2 border-yellow-200">
          <p className="text-sm text-gray-700 leading-relaxed">
            <span className="font-semibold text-yellow-900">⚡ Key Insight:</span> Every year you delay switching to a lower-cost provider, 
            your participants lose not just that year's fee savings, but also all the compound growth those savings would 
            have generated over the remaining years until retirement.
          </p>
        </div>
      </div>

      {/* Explanation Section */}
      <div className="bg-gradient-to-r from-teal/5 to-teal/10 rounded-2xl p-8 border-2 border-teal/20 shadow-soft">
        <h3 className="text-xl font-semibold text-teal-dark mb-4 flex items-center">
          <span className="text-2xl mr-2">📊</span>
          How These Projections Work
        </h3>
        
        <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
          <div className="flex items-start">
            <span className="font-semibold text-teal-dark min-w-[160px]">Return Assumption:</span>
            <span>Based on your plan's historical {
              best25YearData && (best25YearData.assumed_return_rate * 100).toFixed(1)
            }% return rate</span>
          </div>
          
          <div className="flex items-start">
            <span className="font-semibold text-teal-dark min-w-[160px]">Participant Count:</span>
            <span>Projects forward using {
              best25YearData && best25YearData.projected_participants
            } participants</span>
          </div>
          
          <div className="flex items-start">
            <span className="font-semibold text-teal-dark min-w-[160px]">Calculation Method:</span>
            <span>Scenarios show what your plan balance would be at future intervals if you switched to each alternative 
            fee structure today, maintaining the same investment returns but with lower administrative costs.</span>
          </div>

          <div className="flex items-start">
            <span className="font-semibold text-teal-dark min-w-[160px]">Important Note:</span>
            <span>These are projections based on historical data and assumptions. Actual results will vary based on 
            market performance, contribution rates, and participant behavior.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScenariosTab;
